import { useRef, useState } from 'react';
import { deleteWeddingMusic, uploadWeddingMusic } from '@/lib/weddingApi';

interface Props {
  musicUrl: string;
  ownerId: string;
  onChange: (url: string) => void;
}

const MAX_MB = 15;

export default function MusicForm({ musicUrl, ownerId, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    if (!file.type.startsWith('audio/')) {
      setError('음악 파일(mp3, wav, ogg 등)만 올릴 수 있어요.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`음악 파일은 최대 ${MAX_MB}MB까지 올릴 수 있어요.`);
      return;
    }
    setBusy(true);
    try {
      const previous = musicUrl;
      const url = await uploadWeddingMusic(ownerId, file);
      onChange(url);
      if (previous) {
        try {
          await deleteWeddingMusic(previous);
        } catch {
          /* storage cleanup is best-effort */
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '음악 업로드에 실패했어요.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    setError('');
    const previous = musicUrl;
    onChange('');
    if (previous) {
      try {
        await deleteWeddingMusic(previous);
      } catch {
        /* storage cleanup is best-effort */
      }
    }
  };

  return (
    <div className="ed-section">
      <div className="ed-section-head">
        <span className="step">
          <i className="ri-music-2-line" aria-hidden="true" />
        </span>
        <h2>배경음악</h2>
        <span className="hint">청첩장을 열 때 흐르는 음악</span>
      </div>
      <p className="ed-desc">
        음악 파일을 하나 올리면 청첩장 오른쪽 위에 재생 버튼이 생겨요. 하객이 버튼을 눌러 음악을
        켤 수 있어요. (브라우저 정책상 자동 재생은 되지 않아요.)
      </p>

      {musicUrl ? (
        <div className="ed-music-box">
          <div className="ed-music-info">
            <i className="ri-music-2-line" aria-hidden="true" />
            <span>배경음악이 설정됐어요</span>
          </div>
          <audio className="ed-music-audio" controls src={musicUrl} preload="none" />
          <div className="ed-music-actions">
            <button
              type="button"
              className="pf-btn sm"
              onClick={() => !busy && inputRef.current?.click()}
              disabled={busy}
            >
              <i className={busy ? 'ri-loader-4-line spin-anim' : 'ri-refresh-line'} aria-hidden="true" />
              {busy ? '업로드 중…' : '다른 음악으로 바꾸기'}
            </button>
            <button type="button" className="pf-btn sm ghost" onClick={() => void remove()}>
              <i className="ri-delete-bin-line" aria-hidden="true" />
              삭제
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`ed-upload ${busy ? 'is-drag' : ''}`}
          onClick={() => !busy && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
        >
          {busy ? (
            <>
              <i className="ri-loader-4-line spin-anim" aria-hidden="true" />
              <strong>업로드 중…</strong>
            </>
          ) : (
            <>
              <i className="ri-music-2-line" aria-hidden="true" />
              <strong>음악 올리기</strong>
              <span>클릭해서 선택하세요 · MP3 · WAV · OGG (최대 {MAX_MB}MB)</span>
            </>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        hidden
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />

      {error ? (
        <div className="pf-alert err" role="alert" style={{ marginTop: 14 }}>
          <i className="ri-error-warning-line" aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : null}
    </div>
  );
}